<template>
  <b-container
    fluid
    class="ticket-create-bg"
    aria-label="Formulaire de modification de ticket de support logistique"
    role="main"
  >
    <b-row no-gutters>
      <b-col
        cols="12"
        class="px-0 d-flex justify-content-center align-items-center"
        style="min-height: 90vh;"
      >
        <b-card
          class="ticket-create-card shadow-lg border-0"
          style="max-width: 650px; width: 100%; border-radius: 1.25rem; box-shadow: 0 4px 32px 0 rgba(232,0,0,0.10); margin: 3.5rem 0 2.5rem 0;"
        >
          <template #header>
            <div class="d-flex align-items-center mb-2">
              <feather-icon
                icon="Edit3Icon"
                class="mr-2 text-danger"
                size="28"
                aria-label="Modifier le ticket de suivi"
              />
              <span class="h4 font-weight-bold mb-0">Modifier le ticket de suivi</span>
            </div>
            <div class="text-muted small mb-2">
              Merci de modifier le formulaire pour mettre à jour votre demande de support liée à vos colis.
            </div>
            <div
              class="sr-only"
              aria-live="polite"
            >
              Tous les champs marqués d'un astérisque sont obligatoires. Utilisez la touche Tab pour naviguer entre les champs du formulaire.
            </div>
          </template>
          <b-form
            novalidate
            class="ticket-create-form"
            :disabled="formDisabled || form.closed_at"
            aria-label="Formulaire de modification de ticket de support logistique"
            @submit.prevent="onSubmit"
          >
            <!-- Catégorie -->
            <b-form-group
              label="Catégorie du ticket"
              label-for="category"
              label-cols-sm="12"
              label-align-sm="left"
              label-class="font-weight-bold text-dark mb-1"
              description="Sélectionnez la catégorie la plus appropriée."
              :state="categoryState"
              class="mb-4"
            >
              <div class="input-icon-group modern-input">
                <feather-icon
                  icon="TagIcon"
                  size="20"
                  class="input-icon"
                  aria-label="Catégorie du ticket"
                />
                <v-select
                  id="category"
                  v-model="form.category_id"
                  :options="categoryOptions"
                  :reduce="cat => cat.value"
                  :get-option-label="option => option.label"
                  :clearable="false"
                  placeholder="Sélectionner la catégorie"
                  :class="['modern-select', { 'is-invalid': categoryState === false }]"
                  :no-options="categoryOptions.length === 0"
                  no-options-text="Aucune catégorie disponible"
                  aria-required="true"
                  @input="onCategoryChange"
                />
              </div>
              <b-form-invalid-feedback>La catégorie est requise.</b-form-invalid-feedback>
              <div
                v-if="selectedCategoryDescription"
                class="category-description mt-2"
              >
                <feather-icon
                  icon="InfoIcon"
                  size="16"
                  class="mr-1 text-info"
                  aria-label="Description de la catégorie"
                />
                <span>{{ selectedCategoryDescription }}</span>
              </div>
            </b-form-group>

            <!-- Titre -->
            <b-form-group
              label="Titre du ticket"
              label-for="title"
              label-cols-sm="12"
              label-align-sm="left"
              label-class="font-weight-bold text-dark mb-1"
              description="Un titre court et explicite pour votre demande de suivi."
              :state="titleState"
              class="mb-4"
            >
              <div class="input-icon-group modern-input">
                <feather-icon
                  icon="TypeIcon"
                  size="20"
                  class="input-icon"
                  aria-label="Titre du ticket"
                />
                <b-form-input
                  id="title"
                  v-model.trim="form.title"
                  :state="titleState"
                  required
                  placeholder="Saisir le titre du ticket de suivi"
                  maxlength="120"
                  autocomplete="off"
                  aria-required="true"
                />
              </div>
              <b-form-invalid-feedback>Le titre est requis.</b-form-invalid-feedback>
            </b-form-group>
            <!-- Description -->
            <b-form-group
              label="Description du problème"
              label-for="description"
              label-cols-sm="12"
              label-align-sm="left"
              label-class="font-weight-bold text-dark mb-1"
              description="Décrivez précisément le problème ou la demande de suivi."
              :state="descState"
              class="mb-4"
            >
              <vue-editor
                id="description"
                v-model="form.description"
                :state="descState"
                placeholder="Décrire le problème ou la demande de suivi"
                :editor-toolbar="editorToolbar"
                style="min-height: 150px; background: #f8f9fa; border-radius: 0.75rem; border: 1.5px solid #e80000;"
                aria-required="true"
              />
              <b-form-invalid-feedback>La description est requise.</b-form-invalid-feedback>
            </b-form-group>
            <!-- Orders Section -->
            <b-form-group
              label="Commandes liées"
              label-for="orders"
              label-cols-sm="12"
              label-align-sm="left"
              label-class="font-weight-bold text-dark mb-1"
              description="Sélectionnez les commandes concernées par ce ticket."
              :state="ordersState"
              class="mb-4"
            >
              <v-select
                id="orders"
                v-model="form.orders"
                :options="orderOptions"
                :reduce="order => order.value"
                :get-option-label="option => option.label"
                multiple
                :clearable="true"
                placeholder="Sélectionner les commandes"
                :class="['modern-select', { 'is-invalid': ordersState === false }]"
                :no-options="orderOptions.length === 0"
                no-options-text="Aucune commande disponible"
                aria-required="true"
              />
            </b-form-group>
            <!-- Submit -->
            <div class="d-flex flex-column align-items-center mt-4">
              <b-button
                type="submit"
                variant="danger"
                size="lg"
                class="font-weight-bold px-5 py-2 modern-btn modern-btn-animate"
                :disabled="loading || form.closed_at"
                style="border-radius: 0.75rem; font-size: 1.15rem; box-shadow: 0 2px 8px 0 rgba(232,0,0,0.08);"
                aria-label="Enregistrer les modifications du ticket"
              >
                <b-spinner
                  v-if="loading"
                  small
                  class="mr-2"
                />
                Enregistrer les modifications
              </b-button>
            </div>
            <div
              v-if="loading"
              class="form-loading-overlay"
            >
              <b-spinner
                variant="danger"
                label="Chargement..."
              />
            </div>
            <transition name="fade">
              <div
                v-if="showSuccess"
                class="success-feedback"
              >
                <feather-icon
                  icon="CheckCircleIcon"
                  class="mr-2 text-success"
                  size="22"
                  aria-label="Succès"
                />
                Ticket modifié avec succès !
              </div>
            </transition>
          </b-form>
        </b-card>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import axios from '@/libs/axios'
import {
  BContainer, BRow, BCol, BCard, BForm, BFormGroup, BFormInput, BButton, BSpinner, BFormInvalidFeedback,
} from 'bootstrap-vue'
import { VueEditor } from 'vue2-editor'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import 'vue2-editor/dist/vue2-editor.css'

export default {
  components: {
    vSelect, VueEditor, BContainer, BRow, BCol, BCard, BForm, BFormGroup, BFormInput, BButton, BSpinner, BFormInvalidFeedback,
  },
  data() {
    return {
      form: {
        title: '',
        description: '',
        category_id: null,
        orders: [],
        submitter: null,
        status: null,
      },
      categories: [],
      ordersList: [],
      loading: false,
      showSuccess: false,
      editorToolbar: [
        [{ header: [1, 2, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link', 'image'],
        ['clean'],
      ],
      touched: {
        title: false,
        description: false,
        category: false,
        orders: false,
      },
      selectedCategoryDescription: '',
      user: this.$store?.state?.user || {},
      formDisabled: false,
    }
  },
  computed: {
    categoryOptions() {
      return this.categories.map(cat => ({
        value: cat.id,
        label: cat.display_name || cat.name,
        description: cat.description,
      }))
    },
    orderOptions() {
      return this.ordersList.map(order => ({ value: order.id, label: order.reference || `Commande #${order.id}` }))
    },
    titleState() {
      return this.touched.title ? !!this.form.title : null
    },
    descState() {
      return this.touched.description ? !!this.form.description : null
    },
    categoryState() {
      return this.touched.category ? !!this.form.category_id : null
    },
    ordersState() {
      return null
    },
  },
  watch: {
    'form.title': function handleTitle() { this.touched.title = true },
    'form.description': function handleDescription() { this.touched.description = true },
    'form.category_id': function handleCategory() { this.touched.category = true },
    'form.orders': function handleOrders() { this.touched.orders = true },
  },
  async created() {
    await this.fetchOrders()
    await this.fetchCategories()
    await this.fetchTicket()
    // Permission check: supplier can only edit their own ticket and only if status is default
    if (
      this.user?.role === 'supplier'
      && (this.form.submitter?.id !== this.user.id || !this.form.status?.is_default)
    ) {
      this.formDisabled = true
      this.$bvToast.toast('Vous n\'avez pas la permission de modifier ce ticket.', { variant: 'danger' })
    }
  },
  methods: {
    async fetchCategories() {
      const { data } = await axios.get('/api/support/categories/')
      this.categories = data
    },
    async fetchOrders() {
      // Fetch eligible orders
      const { data: eligibleOrders } = await axios.get('/api/orders/eligible-for-ticket/')
      // If ticket is already loaded, merge its orders
      let ticketOrders = []
      if (this.form && this.form.orders && this.form.orders.length > 0) {
        // If orders are objects, map to id; if already ids, keep as is
        ticketOrders = this.form.orders.map(o => (typeof o === 'object' ? o.id : o))
      }
      // Merge eligible orders with ticket's current orders (avoid duplicates)
      const merged = [...eligibleOrders]
      // Add missing ticket orders
      ticketOrders.forEach(orderId => {
        if (!merged.some(o => o.id === orderId)) {
          // Try to find full order object from ticket (if loaded)
          const orderObj = (this.ticketOrdersFull || []).find(o => o.id === orderId)
          if (orderObj) {
            merged.push(orderObj)
          }
        }
      })
      this.ordersList = merged
    },
    async fetchTicket() {
      const ticketId = this.$route.params.id
      try {
        const { data } = await axios.get(`/api/support/tickets/${ticketId}/`)
        if (data.closed_at) {
          this.$bvToast.toast('Ce ticket est déjà fermé et ne peut plus être modifié.', { variant: 'danger' })
          this.$router.replace({ name: 'SupportTicketDetail', params: { id: ticketId } })
          return
        }
        this.form = {
          ...data,
          orders: data.orders.map(order => order.id), // Ensure orders is an array of ids
          category_id: data.category ? data.category.id : null, // Ensure category_id is set to the id
        }
        // Set touched states
        this.touched.title = true
        this.touched.description = true
        this.touched.category = true
        this.touched.orders = true
      } catch (e) {
        if (e.response && e.response.status === 403) {
          this.$bvToast.toast('Ce ticket est déjà fermé et ne peut plus être modifié.', { variant: 'danger' })
          this.$router.replace({ name: 'SupportTicketDetail', params: { id: ticketId } })
        } else {
          this.$bvToast.toast('Erreur lors du chargement du ticket.', { variant: 'danger' })
        }
      }
    },
    onCategoryChange() {
      const selectedCategory = this.categories.find(cat => cat.id === this.form.category_id)
      this.selectedCategoryDescription = selectedCategory ? selectedCategory.description : ''
    },
    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleString('fr-FR', {
        year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
      })
    },
    async onSubmit() {
      if (this.form.closed_at) {
        this.$bvToast.toast('Ce ticket est déjà fermé et ne peut plus être modifié.', { variant: 'danger' })
        return
      }
      this.loading = true
      try {
        const ticketId = this.$route.params.id
        await axios.put(`/api/support/tickets/${ticketId}/`, this.form)
        this.showSuccess = true
        // Redirect to detail page after update
        setTimeout(() => {
          this.$router.push({ name: 'SupportTicketDetail', params: { id: ticketId } })
        }, 1200)
      } catch (error) {
        this.$bvToast.toast('Erreur lors de la modification du ticket.', { variant: 'danger' })
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.ticket-create-bg {
  background-color: #f2f2f2;
}

.ticket-create-card {
  background-color: #ffffff;
  border-radius: 0.75rem;
  overflow: hidden;
}

.ticket-create-form {
  padding: 2rem;
}

.input-icon-group {
  position: relative;
}

.input-icon {
  position: absolute;
  top: 50%;
  left: 12px;
  transform: translateY(-50%);
  pointer-events: none;
  z-index: 1;
  color: #e80000;
}

.input-icon-group .form-control {
  padding-left: 2.25rem;
}

.input-icon-group ::v-deep .vs__dropdown-toggle {
  padding-left: 2.25rem;
}

.modern-select {
  border: 1.5px solid #e80000;
  border-radius: 0.375rem;
}

.modern-btn {
  transition: background-color 0.3s, transform 0.3s;
}

.modern-btn-animate:hover {
  background-color: #c70000;
  transform: translateY(-2px);
}

.success-feedback {
  display: flex;
  align-items: center;
  padding: 0.75rem 1.25rem;
  margin-top: 1rem;
  border-radius: 0.375rem;
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.form-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 0.75rem;
  z-index: 10;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0,0,0,0);
  border: 0;
}
</style>
